import { Box, Typography, IconButton } from '@mui/material'
import { styled } from '@mui/material/styles'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import { Link as RouterLink } from 'react-router-dom'
import LazyImage from '../lazy-image'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import FavoriteIcon from '@mui/icons-material/Favorite'
import PlaylistAddRoundedIcon from '@mui/icons-material/PlaylistAddRounded'
import PlaylistAddCheckRoundedIcon from '@mui/icons-material/PlaylistAddCheckRounded'
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import { useMediaState } from '../../context/media-state-context'
import { useAuth } from '../../context/AuthContext'

interface SmallCardProps {
  id: number
  type: 'movie' | 'show'
  title: string
  year: string
  rating: number
  posterUrl?: string
  genres?: string[]
}

// A real anchor so right-click / middle-click / cmd+click open in a new tab.
const StyledCardWrapper = styled(RouterLink)(({ theme }) => ({
  display: 'block',
  textDecoration: 'none',
  color: 'inherit',
  cursor: 'pointer',
  width: 160,
  flexShrink: 0,
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  transition: 'transform 0.3s ease',
  '@media (hover: hover) and (pointer: fine)': {
    '&:hover': {
      transform: 'scale(1.03)',
      '& .card-poster': {
        borderColor: 'transparent',
        backgroundImage: `linear-gradient(${theme.palette.background.paper}, ${theme.palette.background.paper}), linear-gradient(135deg, #8B1E3F 0%, #8B1E3F 50%, #4A0E1F 100%)`,
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
      },
      '& .card-overlay': {
        opacity: 1,
      },
    },
  },
}))

const StyledPoster = styled(Box)(({ theme }) => ({
  width: 160,
  height: 240,
  borderRadius: Number(theme.shape.borderRadius) * 2,
  overflow: 'hidden',
  position: 'relative',
  border: '2px solid',
  borderColor: theme.palette.divider,
  transition: 'all 0.5s ease',
  backgroundColor: theme.palette.background.default,
}))

const StyledPosterFallback = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 48,
  backgroundColor: theme.palette.background.default,
}))

const StyledOverlay = styled(Box)({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, transparent 100%)',
  padding: 12,
  opacity: 0,
  transition: 'opacity 0.3s ease',
})

const StyledOverlayText = styled(Typography)({
  color: '#fff',
  fontSize: 12,
  fontWeight: 500,
})

const StyledInfoWrapper = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
}))

const StyledTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: 14,
  fontWeight: 500,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}))

const StyledMetaWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(0.5),
}))

const StyledYear = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 12,
}))

interface StyledRatingProps {
  value: number
}

const StyledRating = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'value',
})<StyledRatingProps>(({ theme, value }) => {
  let color = theme.palette.green.main

  if (value < 4) {
    color = theme.palette.error.main
  } else if (value < 7) {
    color = theme.palette.warning.main
  }

  return {
    color,
    fontSize: 13,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  }
})

const StyledNotYetRated = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: 12,
  fontWeight: 600,
  fontStyle: 'italic',
}))

const CardActions = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(0.5),
  right: theme.spacing(0.5),
  display: 'flex',
  gap: 4,
  zIndex: 2,
  padding: theme.spacing(0.5),
  backgroundColor: 'rgba(0,0,0,0.6)',
  borderRadius: 12,
}))

// Poster action buttons: white by default, colored once the item is in the
// matching list. Watchlist greys out when the title is already watched.
const WatchedIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ active }) => ({
  color: active ? '#2e7d32' : '#fff',
}))

const FavoriteIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ active }) => ({
  color: active ? '#e53935' : '#fff',
}))

const WatchlistIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ active }) => ({
  color: active ? '#fbc02d' : '#fff',
  '&.Mui-disabled': {
    color: 'rgba(255,255,255,0.6)',
    opacity: 0.6,
  },
}))

const SmallCard = ({
  id,
  type,
  title,
  year,
  rating,
  posterUrl,
  genres = [],
}: SmallCardProps) => {
  const {
    favoritesSet,
    watchlistSet,
    historySet,
    toggleFavorite,
    toggleWatchlist,
    toggleHistory,
  } = useMediaState()
  const { isAuthenticated } = useAuth()
  const tmdbId = id

  const meta = {
    title,
    posterPath: posterUrl,
    releaseYear: year ? Number(year) : undefined,
    genres: genres ?? [],
  }

  return (
    <StyledCardWrapper to={`/details/${type}/${id}`}>
      <StyledPoster className='card-poster'>
        {isAuthenticated && (
          <CardActions>
            <WatchedIconButton
              size='small'
              active={historySet.has(tmdbId)}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                toggleHistory(tmdbId, type, meta)
              }}
            >
              {historySet.has(tmdbId) ? (
                <AccessTimeFilledIcon fontSize='small' />
              ) : (
                <AccessTimeOutlinedIcon fontSize='small' />
              )}
            </WatchedIconButton>
            <FavoriteIconButton
              size='small'
              active={favoritesSet.has(tmdbId)}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                toggleFavorite(tmdbId, type, meta)
              }}
            >
              {favoritesSet.has(tmdbId) ? (
                <FavoriteIcon fontSize='small' />
              ) : (
                <FavoriteBorderIcon fontSize='small' />
              )}
            </FavoriteIconButton>
            <WatchlistIconButton
              size='small'
              active={watchlistSet.has(tmdbId)}
              disabled={historySet.has(tmdbId)}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                toggleWatchlist(tmdbId, type, meta)
              }}
            >
              {watchlistSet.has(tmdbId) ? (
                <PlaylistAddCheckRoundedIcon fontSize='small' />
              ) : (
                <PlaylistAddRoundedIcon fontSize='small' />
              )}
            </WatchlistIconButton>
          </CardActions>
        )}
        <LazyImage
          src={posterUrl}
          alt={title}
          fallback={
            <StyledPosterFallback>
              {title.slice(0, 1).toUpperCase() || '?'}
            </StyledPosterFallback>
          }
        />

        <StyledOverlay className='card-overlay'>
          <StyledOverlayText>View Details {'>'}</StyledOverlayText>
        </StyledOverlay>
      </StyledPoster>

      <StyledInfoWrapper>
        <StyledTitle>{title}</StyledTitle>
        <StyledMetaWrapper>
          <StyledYear>{year}</StyledYear>
          {rating ? (
            <StyledRating value={rating}>
              <StarRoundedIcon fontSize='inherit' />
              {rating.toFixed(1)}
            </StyledRating>
          ) : (
            <StyledNotYetRated>Not yet rated</StyledNotYetRated>
          )}
        </StyledMetaWrapper>
      </StyledInfoWrapper>
    </StyledCardWrapper>
  )
}

export default SmallCard
